import { ReactNode, useEffect, useState } from 'react';

import BackupTableIcon from '@mui/icons-material/BackupTable';
import LanguageIcon from '@mui/icons-material/Language';
import SpeedIcon from '@mui/icons-material/Speed';
import { Link, NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GetAvailableRoutes } from 'containers/AppRouter';
import { PAGE_DASHBOARD } from 'containers/Dashboard';
import { PAGE_FLOWS } from 'containers/Flows';
import { PAGE_INSTANCE_SETTINGS } from 'containers/InstanceSettings';
import { PAGE_IP_NETWORKS } from 'containers/IPNetworks';
import { PAGE_LOGIN } from 'containers/Login';
import { PAGE_ORGANISATIONS } from 'containers/Organizations';
import { PAGE_SCHC_TEMPLATES } from 'containers/SCHCTemplates';
import { PAGE_SOURCES } from 'containers/Sources';
import { ConditionalTooltip } from 'shared/components/Conditional';
import { PAGE_USERS } from 'shared/containers/GenericUsers';
import { GeneralInformationT } from 'shared/models/general-system';
import accessToken from 'utils/accessToken';
import api from 'utils/api';
import { useAuth } from 'utils/hooks/providers/auth-context';

import './Menu.scss';

type MenuItemProps = {
  title: string;
  icon?: ReactNode;
  svgName?: string;
  linkTo: string;
  allowedPages?: Array<string>;
  menuIsOpen?: boolean;
}

function MenuItem({
  title,
  icon,
  svgName,
  linkTo,
  allowedPages = [],
  menuIsOpen,
}: MenuItemProps) {
  if (allowedPages.length && !allowedPages.includes(linkTo)) {
    return null;
  }
  return (
    <ConditionalTooltip title={!menuIsOpen ? title : ''} placement="right">
      <li>
        <NavLink to={linkTo} className={(isActive: boolean) => `menu--section__item ${isActive ? 'active' : ''}`}>
          {
            icon || (
              <svg>
                <use xlinkHref={`/icons/_sprite.svg#${svgName}`} />
              </svg>
            )
          }
          <p>{title}</p>
        </NavLink>
      </li>
    </ConditionalTooltip>
  );
}

function Menu() {
  const { authState } = useAuth();
  const allowedPages = GetAvailableRoutes(authState);

  const [version, setVersion] = useState('.');
  const [isOpen, setIsOpen] = useState(true);

  const logout = () => {
    api.auth.logout().then();
    accessToken.set('');
  };

  const loadVersion = () => {
    api.general.getVersion()
      .then((resp: Response) => {
        if (resp.status >= 400) {
          toast.error(`Failed to get application version - Error ${resp.status}`);
          setVersion('');
        } else {
          resp.json()
            .then((data: GeneralInformationT) => {
              setVersion(data.version);
            });
        }
      });
  };

  useEffect(() => {
    loadVersion();
  }, []);

  return (
    <div className={`menu box ${isOpen ? '' : 'collapsed'}`}>
      <button
        type="button"
        className="menu--toggle"
        onClick={() => setIsOpen((status) => !status)}
      >
        <p style={{ transform: isOpen ? '' : 'scaleX(-1)' }}>‹</p>
      </button>
      <Link to="/">
        <img src="/images/acklio_logo.svg" alt="Logo Acklio" className="menu--logo" />
        <img src="/images/acklio_logo_small.svg" alt="Logo Acklio" className="menu--logo--small" />
      </Link>
      <h4 className="menu--version">{ version }</h4>
      <div className="menu--section">
        <nav>
          <ul>
            <MenuItem title="Dashboard" icon={<SpeedIcon />} linkTo={PAGE_DASHBOARD} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <MenuItem title="Instance Settings" svgName="settings" linkTo={PAGE_INSTANCE_SETTINGS} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <div className="separator" />
            <MenuItem title="Organizations" svgName="building" linkTo={PAGE_ORGANISATIONS} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <MenuItem title="Users" svgName="user" linkTo={PAGE_USERS} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <div className="separator" />
            <MenuItem title="Flows" svgName="flow" linkTo={PAGE_FLOWS} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <MenuItem title="Sources" svgName="folder" linkTo={PAGE_SOURCES} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <MenuItem title="IP Networks" icon={<LanguageIcon />} linkTo={PAGE_IP_NETWORKS} allowedPages={allowedPages} menuIsOpen={isOpen} />
            <MenuItem title="SCHC Templates" icon={<BackupTableIcon />} linkTo={PAGE_SCHC_TEMPLATES} allowedPages={allowedPages} menuIsOpen={isOpen} />
          </ul>
        </nav>
      </div>
      <div className="menu--bottom">
        <Link to={PAGE_LOGIN} className="menu--bottom__logout" onClick={logout}>
          <svg className="menu--bottom__logout__icon">
            <use xlinkHref="/icons/_sprite.svg#logout" xlinkTitle="Logout" />
          </svg>
          <p>Logout</p>
        </Link>
      </div>
    </div>
  );
}

export default Menu;
